import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Menu, X, MessageSquarePlus } from 'lucide-react';
import logo from '@/assets/logo.png';
import { useAuth } from '@/hooks/useAuth';
import { useUserRole } from '@/hooks/useUserRole';
import { IssueTrackerDrawer } from '@/components/admin/IssueTrackerDrawer';

const Header = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [issueTrackerOpen, setIssueTrackerOpen] = useState(false);
  const { user, loading: authLoading, signOut } = useAuth();
  const { isAdmin, loading: roleLoading } = useUserRole();

  const currentLang = i18n.language;
  const pathWithoutLang = location.pathname.replace(/^\/(dk|en)/, '') || '/';

  const changeLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('language', lang);
    navigate(`/${lang}${pathWithoutLang}`);
  };

  const handleSignOut = async () => {
    await signOut();
    navigate(`/${currentLang}`);
  };

  const navItems = [
    { key: 'services', path: '/services' },
    { key: 'methodology', path: '/methodology' },
    { key: 'cases', path: '/cases' },
    { key: 'about', path: '/about' },
    { key: 'blog', path: '/blog' },
    { key: 'contact', path: '/contact' },
  ];

  const isActive = (path: string) => {
    const fullPath = `/${currentLang}${path}`;
    return location.pathname === fullPath;
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <Link to={`/${currentLang}`} className="flex items-center">
            <img src={logo} alt="FraimeWorks" className="h-8 w-auto" width="175" height="32" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-1">
            {navItems.map((item) => (
              <Link
                key={item.key}
                to={`/${currentLang}${item.path}`}
                className={`px-3 py-2 text-sm font-medium rounded-md transition-base ${
                  isActive(item.path)
                    ? 'text-primary bg-primary/10'
                    : 'text-foreground hover:text-primary hover:bg-muted'
                }`}
              >
                {t(`nav.${item.key}`)}
              </Link>
            ))}
            {user && !authLoading && isAdmin && !roleLoading && (
              <>
                <Link
                  to={`/${currentLang}/admin`}
                  className={`px-3 py-2 text-sm font-medium rounded-md transition-base ${
                    isActive('/admin')
                      ? 'text-primary bg-primary/10'
                      : 'text-foreground hover:text-primary hover:bg-muted'
                  }`}
                >
                  {t('nav.admin')}
                </Link>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIssueTrackerOpen(true)}
                  className="relative"
                >
                  <MessageSquarePlus className="h-4 w-4 mr-1" />
                  Issues
                </Button>
              </>
            )}
          </nav>

          {/* Language Switcher, Sign Out & Mobile Menu */}
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2 text-sm">
              <button
                onClick={() => changeLanguage('dk')}
                className={`transition-base ${
                  currentLang === 'dk' ? 'text-primary font-semibold' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                🇩🇰 DK
              </button>
              <span className="text-border">|</span>
              <button
                onClick={() => changeLanguage('en')}
                className={`transition-base ${
                  currentLang === 'en' ? 'text-primary font-semibold' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                🇬🇧 EN
              </button>
            </div>

            {/* Sign Out Button - Only for logged in users */}
            {user && !authLoading && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleSignOut}
                className="hidden md:flex"
              >
                {t('nav.signOut')}
              </Button>
            )}

            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X /> : <Menu />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <nav className="md:hidden pb-4 pt-2 space-y-1">
            {navItems.map((item) => (
              <Link
                key={item.key}
                to={`/${currentLang}${item.path}`}
                onClick={() => setMobileMenuOpen(false)}
                className={`block px-3 py-2 text-base font-medium rounded-md transition-base ${
                  isActive(item.path)
                    ? 'text-primary bg-primary/10'
                    : 'text-foreground hover:text-primary hover:bg-muted'
                }`}
              >
                {t(`nav.${item.key}`)}
              </Link>
            ))}
            {user && !authLoading && isAdmin && !roleLoading && (
              <Link
                to={`/${currentLang}/admin`}
                onClick={() => setMobileMenuOpen(false)}
                className={`block px-3 py-2 text-base font-medium rounded-md transition-base ${
                  isActive('/admin')
                    ? 'text-primary bg-primary/10'
                    : 'text-foreground hover:text-primary hover:bg-muted'
                }`}
              >
                {t('nav.admin')}
              </Link>
            )}
            {user && !authLoading && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleSignOut}
                className="w-full justify-start"
              >
                {t('nav.signOut')}
              </Button>
            )}
          </nav>
        )}
      </div>

      <IssueTrackerDrawer open={issueTrackerOpen} onOpenChange={setIssueTrackerOpen} />
    </header>
  );
};

export default Header;
